﻿import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import MUIModal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';

import { CloseIcon } from 'components/Icon';

import * as actions from 'components/Modal/Modal.ducks';

import theme from "./Modal.module.sass";

const SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
};

const Modal = forwardRef(
  (
    {
      className,
      open,
      title,
      autoClose = false,
      children,
      size = SIZES.medium,
      showTopActions = true,
      setOpenModalId,
      onClose = () => undefined,
    },
    ref,
  ) => (
    <MUIModal
      className={classNames(className, theme.Modal, theme[size])}
      ref={ref}
      open={open}
      onClose={(ev) => {
        ev.preventDefault();
        if (autoClose) {
          setOpenModalId(null);
        }
        onClose();
      }}
      closeAfterTransition
      /*
        Keep MUI's own backdrop and only tune its timing. MUI v7's Modal styles its
        default backdrop with z-index -1, so it sits under the content. Passing the
        plain Backdrop as BackdropComponent, the v4 idiom, swapped in one with no
        z-index: v4's Backdrop carried z-index -1 itself and v5 dropped it. Any
        content that is not positioned then lost to the backdrop the moment its fade
        ended, and every click on it closed the modal instead.
      */
      slotProps={{ backdrop: { timeout: 500 } }}
    >
      <Fade in={open}>
        <div className={theme.Content}>
          <div className={theme.Top}>
            {title && <h1 className={theme.Title}>{title}</h1>}
            {showTopActions && (
              <div className={theme.Actions}>
                <CloseIcon
                  data-testid="closeIcon"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setOpenModalId(null);
                    onClose();
                  }}
                />
              </div>
            )}
          </div>
          <div className={theme.Body}>{React.Children.only(children)}</div>
        </div>
      </Fade>
    </MUIModal>
  ),
);

Modal.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  autoClose: PropTypes.bool,
  showTopActions: PropTypes.bool,
  children: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  onClose: PropTypes.func,
  setOpenModalId: PropTypes.func.isRequired,
};

const mapStateToProps = ({ modal }, ownProps) => ({
  open: ownProps.open || ownProps.id === modal.openModalId,
});

export default connect(
  mapStateToProps,
  {
    ...actions,
  },
  undefined,
  { forwardRef: true },
)(Modal);
